/**
 * Shared geo (geo.api.gouv.fr) commune data layer.
 *
 * The SINGLE place that talks to geo.api.gouv.fr: one `Commune` type, one set of
 * requested fields, one normalizer, one base URL. The React hook
 * (`useCommuneSearch`) and the birth-place auto-fill both build on this; the
 * legacy `useSearchCity` (birth-place section) folds in here during the
 * birth-place modal redesign via a `searchCommunesByPostal` sibling.
 *
 * Pure (no React) — easy to unit-test as plain async functions.
 */

export interface Commune {
  /** INSEE COG code — e.g. "63113". */
  code: string;
  /** Commune name — e.g. "Clermont-Ferrand". */
  nom: string;
  /** Department code — e.g. "63". */
  codeDepartement: string;
  /** Department name — e.g. "Puy-de-Dôme". */
  departementNom: string;
  /** Postal code(s) of the commune. */
  codesPostaux: string[];
}

const GEO_API_URL = 'https://geo.api.gouv.fr/communes';
/** Fields requested for every call, so the normalized `Commune` is complete. */
const FIELDS = 'code,nom,codeDepartement,departement,codesPostaux';
const DEFAULT_LIMIT = 10;

/** Map a raw geo.api.gouv.fr commune object onto our normalized `Commune`. */
const normalize = (data: {
  code: string;
  nom: string;
  codeDepartement?: string;
  departement?: { code?: string; nom?: string };
  codesPostaux?: string[];
}): Commune => ({
  code: data.code,
  nom: data.nom,
  codeDepartement: data.codeDepartement || data.departement?.code || '',
  departementNom: data.departement?.nom || '',
  codesPostaux: Array.isArray(data.codesPostaux) ? data.codesPostaux : [],
});

/**
 * Search communes by (partial) name — the autocomplete source.
 *
 * Throws on a non-ok HTTP response and propagates `AbortError` when the request
 * is superseded, so the caller (the hook) can distinguish abort vs. real error.
 */
export const searchCommunesByName = async (
  query: string,
  opts: { signal?: AbortSignal; limit?: number } = {}
): Promise<Commune[]> => {
  const trimmed = (query || '').trim();
  if (!trimmed) return [];

  const url =
    `${GEO_API_URL}?nom=${encodeURIComponent(trimmed)}` +
    `&fields=${FIELDS}&limit=${opts.limit ?? DEFAULT_LIMIT}&boost=population`;

  const res = await fetch(url, opts.signal ? { signal: opts.signal } : {});
  if (!res.ok) throw new Error(`geo.api.gouv.fr ${res.status}`);

  const data = await res.json();
  return Array.isArray(data) ? data.map(normalize) : [];
};

/**
 * In-memory cache for by-CODE lookups only (never the by-name search).
 *
 * Commune-by-code is deterministic and the data is static, so a resolved
 * commune — or a definitive "unknown code" (404, e.g. a foreign 99xxx COG-Pays
 * code) — is reused for the rest of the session. This lets the INSi search
 * preview and the patient create/edit auto-fill share a SINGLE network request
 * for the same code. Transient failures (network / 5xx) are deliberately NOT
 * cached, so they can never poison a later retry.
 */
const codeCache = new Map<string, Commune | null>();

/** Test-only: clear the by-code cache so cases don't leak across each other. */
export const _resetCommuneCache = (): void => {
  codeCache.clear();
};

/**
 * Resolve a single commune from its INSEE code (the inverse of the name search).
 *
 * Used to auto-fill the birth place from the INSEE code INSi returns
 * (`tiq.lieuNaissance`). Returns null on any failure or for a code
 * geo.api.gouv.fr doesn't know — notably foreign COG-Pays codes (99xxx) — so
 * callers fall back to manual entry rather than blocking.
 */
export const lookupCommuneByCode = async (
  code: string
): Promise<Commune | null> => {
  const trimmed = (code || '').trim();
  if (!trimmed) return null;
  if (codeCache.has(trimmed)) return codeCache.get(trimmed) ?? null;

  try {
    const res = await fetch(
      `${GEO_API_URL}/${encodeURIComponent(trimmed)}?fields=${FIELDS}`
    );
    if (!res.ok) {
      // 404 = the code is definitively not a French commune → safe to remember.
      // Any other non-ok (5xx) is transient → don't cache, so a retry can work.
      if (res.status === 404) codeCache.set(trimmed, null);
      return null;
    }

    const data = await res.json();
    if (!data || !data.code) {
      codeCache.set(trimmed, null);
      return null;
    }

    const commune = normalize(data);
    codeCache.set(trimmed, commune);
    return commune;
  } catch {
    // Network/transient error — return null but DON'T cache (allow retry).
    return null;
  }
};

export default lookupCommuneByCode;
